import { Component, OnInit, OnChanges, SimpleChanges, Input, ViewChild, ElementRef, inject, DestroyRef } from '@angular/core';
import { GetImage, GetStripeId, Product } from 'src/app/models/product.model';
import { reducedPrice } from 'src/app/utils/animation.utils';
import { User } from 'src/app/models/user.model';
import { AccountService } from '../services/account.service';
import { LoginModalComponent } from '../login/login-modal/login-modal.component';
import { TermsAndConditionsModalComponent } from '../terms-and-conditions-modal/terms-and-conditions-modal.component';
import { PaymentsService } from '../services/payments.service';
import { ModalService } from '../services/modal.service';
import { interval, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Router, RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';

export { reducedPrice } from 'src/app/utils/animation.utils';

interface timeComponents {
  secondsToDday: number;
  minutesToDday: number;
  hoursToDday: number;
  daysToDday: number;
  monthToDday: number;
}

@Component({
    selector: 'app-product',
    templateUrl: './product.component.html',
    styleUrls: ['./product.component.css'],
    imports: [NgClass, RouterLink]
})
export class ProductComponent implements OnInit, OnChanges {
  @ViewChild('productContainer') Container: ElementRef;
  @ViewChild('header_img') Image: ElementRef;

  private accountService = inject(AccountService);
  private paymentsService = inject(PaymentsService);
  private modalService = inject(ModalService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  @Input() product: Product;
  @Input() showDetail: boolean;
  productImagePath: string;
  error: Error;
  user: User;

  public timeLeft$: Observable<timeComponents>;
  timeDiff = 1;

  ngOnInit(): void {
    this.accountService.userObservable
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        {
          next: user => {
            if (user) {
              this.user = user;
            }
          },
          error: err => {
            this.error = err;
          }
        }
      )
    this.SetImage();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('product' in changes) {
      this.timeLeft$ = interval(1000).pipe(
        map((_x) => this.calcDateDiff()),
        shareReplay(1)
      );
    }
  }

  onBuyEvent(product: Product) {
    const itemId = GetStripeId(product);
    if (this.user?.isLogin) {
      if (!this.user.has_accepted_termsAndConditions) {
        const termsAndConditionsModal = this.modalService.open(TermsAndConditionsModalComponent, { size: 'lg', modalDialogClass: 'modal-dialog-centered' });
        termsAndConditionsModal.componentInstance.SetItemId(itemId);
      }
      else {
        this.paymentsService.checkout(itemId).subscribe(
          () => {
          },
          (error) => {
            this.error = error;
          }
        )
      }
    } else {
      const _loginModal = this.modalService.open(LoginModalComponent, { size: 'lg', modalDialogClass: 'modal-dialog-centered' });
    }
  }

  mouseOverBoundingElem(evt: MouseEvent) {
    const bounding = this.Container.nativeElement.getBoundingClientRect();
    const x = evt.clientX - Math.round(bounding.left);
    const y = evt.clientY - Math.round(bounding.top);

    return {
      x: Math.max(0, x),
      y: Math.max(0, y),
      width: Math.round(bounding.width),
      height: Math.round(bounding.height)
    };
  }

  over(_event: MouseEvent) {
    const style = this.Container.nativeElement.style;
    if (style) {
      style.transform = `scale(1.2)`;
      style.filter = `brightness(1.2)`;
      style.zIndex = '2';
    }
    this.Image.nativeElement.style.transform = `scale(1.2)`;
  }

  move(event: MouseEvent) {
    const maxTilt = 15;
    const bounding = this.mouseOverBoundingElem(event);
    const posX = bounding.width / 2 - bounding.x;
    const posY = bounding.height / 2 - bounding.y;
    const hypotenuseCursor = Math.sqrt(Math.pow(posX, 2) + Math.pow(posY, 2));
    const hypotenuseMax = Math.sqrt(Math.pow(bounding.width / 2, 2) + Math.pow(bounding.height / 2, 2));
    const ratio = hypotenuseCursor / hypotenuseMax;

    const style = this.Container.nativeElement.style;

    style.transform = `rotate3d(${posY / hypotenuseCursor}, ${-posX / hypotenuseCursor}, 0, ${ratio * maxTilt}deg)
                       scale(1.2)
                       translateX(${posX * ratio * 0.02}px) translateY(${posY * ratio * 0.02}px)`

    style.filter = `brightness(${this.clamp(1 + bounding.y / bounding.height, 1.1, 1.4)})`;
  }

  OpenInfo() {
    this.router.navigate(['/shop', this.product.id]);
  }

  out(_event: MouseEvent) {
    const style = this.Container.nativeElement.style;
    style.transform = '';
    style.filter = '';
    style.zIndex = '1';
    this.Image.nativeElement.style.transform = '';
  }

  clamp(num: number, min: number, max: number) {
    return num <= min
      ? min
      : num >= max
        ? max
        : num
  }

  getReducedPrice(product: Product) {
    return reducedPrice(product)
  }

  calcDateDiff(): timeComponents {
    const milliSecondsInASecond = 1000;
    const hoursInADay = 24;
    const minutesInAnHour = 60;
    const secondsInAMinute = 60;
    const dayInMonth = 30;
    const timeDifference = new Date(this.product.availabilityDate).getTime().valueOf() - Date.now();
    this.timeDiff = timeDifference;
    const monthToDday = Math.floor(timeDifference / (milliSecondsInASecond * minutesInAnHour * secondsInAMinute * hoursInADay * dayInMonth));
    const daysToDday = Math.floor(timeDifference / (milliSecondsInASecond * minutesInAnHour * secondsInAMinute * hoursInADay) % dayInMonth);
    const hoursToDday = Math.floor((timeDifference / (milliSecondsInASecond * minutesInAnHour * secondsInAMinute)) % hoursInADay);
    const minutesToDday = Math.floor((timeDifference / (milliSecondsInASecond * minutesInAnHour)) % secondsInAMinute);
    const secondsToDday = Math.floor(timeDifference / milliSecondsInASecond) % secondsInAMinute;
    return { secondsToDday, minutesToDday, hoursToDday, daysToDday, monthToDday };
  }

  SetImage() {
    this.productImagePath = GetImage(this.product);
  }
}
